import { normalizeIpId } from '$/domains/myIp/myIpService';
import { validateMyIpRoomId } from '$/domains/myIp/agentBinding';
import type { MyIpProject } from '$/domains/myIp/types';

/** 与现有 Studio 导航保持一致：本地使用项目前缀，部署环境使用公开路径。 */
export function getMyIpPaths(development: boolean) {
  const prefix = development ? '/--roboneo' : '';
  return { prefix, list: `${prefix}/myips`, home: `${prefix}/home` };
}

/** 卡片地址只接受已校验的十进制字符串，uint64 不经过 Number 转换。 */
export function getMyIpDetailHref(listPath: string, ipId: string): string {
  return `${listPath}/${normalizeIpId(ipId)}`;
}

/** 从返回地址取出画布身份，只给编辑埋点使用，不把来源 URL 当成创建交接。 */
export function getMyIpCanvasQueryFromHref(href: string): { projectId?: string; canvasId?: string; } {
  try {
    const url = new URL(href, 'https://my-ip.invalid');
    const projectId = url.searchParams.get('project_id')?.trim() || undefined;
    const canvasId = url.searchParams.get('canvas_id')?.trim() || undefined;
    return { projectId, canvasId };
  } catch {
    return {};
  }
}

/** 创建链接携带 IP 与 Agent 房间；已知画布时另附 project_id、canvas_id 供编辑埋点回填。 */
export function getMyIpCreationHref<Prefix extends string>(
  prefix: Prefix,
  ipId: string,
  roomId: string,
  canvas?: { projectId?: string; canvasId?: string; },
): `${Prefix}/myips/create?${string}` {
  const query = new URLSearchParams({ ip_id: normalizeIpId(ipId), room_id: validateMyIpRoomId(roomId) });
  if (canvas?.projectId?.trim()) query.set('project_id', canvas.projectId.trim());
  if (canvas?.canvasId?.trim()) query.set('canvas_id', canvas.canvasId.trim());
  return `${prefix}/myips/create?${query}`;
}

/** 沿用个人画布/工作流与 V3 Agent 项目导航；未知类型不猜路由，也不拿项目 ID 冒充房间 ID。 */
export function getMyIpProjectHref(prefix: string, project: MyIpProject): string | undefined {
  let editor: string;
  switch (project.roomType) {
    case 'canvas': editor = 'studio'; break;
    case 'workflow': editor = 'ai_flow'; break;
    case 'agent': editor = 'team_studio'; break;
    default: return undefined;
  }
  if (!project.roomId) return undefined;
  const query = new URLSearchParams({ room_id: project.roomId });
  // 与 AgentTeamProjects.handleProjectClick 相同，项目上下文不能省略或与房间 ID 混用。
  if (project.roomType === 'agent' && project.projectId) query.set('project_id', project.projectId);
  return `${prefix}/${editor}?${query.toString()}`;
}
